import { useState } from 'react';
import { ConfigurationPanel } from './configuration-panel';
import { QueryPanel } from './query-panel';
import { useAgentRetrievalTesting } from '../../../hooks/use-agents';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import type {
  IRetrievalQueryResult,
  IRetrievalAnalysisData,
} from '../../types/agent.service.type';

type AgentsRetrievalTestingProps = {
  agentId: string;
};

export const AgentsRetrievalTesting = ({ agentId }: AgentsRetrievalTestingProps) => {
  const [enableEnhancement, setEnableEnhancement] = useState(true);
  const [relevanceScore, setRelevanceScore] = useState(0.7);
  const [recallNumber, setRecallNumber] = useState(10);
  const [searchType, setSearchType] = useState<'mixed' | 'semantic'>('mixed');
  const [semanticWeight, setSemanticWeight] = useState(90);
  const [denseWeight, setDenseWeight] = useState(50);
  const [keywordWeight, setKeywordWeight] = useState(50);
  const [rerankModel, setRerankModel] = useState(true);
  const [vectorQuery, setVectorQuery] = useState('');
  const [groundTruthTexts, setGroundTruthTexts] = useState<string[]>([]);
  const [hasResults, setHasResults] = useState(false);
  const [activeTab, setActiveTab] = useState('initial');
  const [results, setResults] = useState<IRetrievalQueryResult[]>([]);
  const [analysisData, setAnalysisData] = useState<IRetrievalAnalysisData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const { mutateAsync: testRetrieval } = useAgentRetrievalTesting(agentId);
  const { toast } = useToast();

  const hasEmptyGroundTruthError =
    groundTruthTexts.length > 0 && groundTruthTexts.every((text) => text.trim() === '');

  const handleRunQuery = async () => {
    if (!vectorQuery.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a query',
        variant: 'destructive',
      });
      return;
    }

    if (!blocksKey) {
      toast({
        title: 'Error',
        description: 'Project key not found',
        variant: 'destructive',
      });
      return;
    }

    if (hasEmptyGroundTruthError) {
      toast({
        title: 'Error',
        description: 'Please enter at least one non-empty ground truth text or disable this option',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await testRetrieval({
        project_key: blocksKey,
        enable_query_enhancement: enableEnhancement,
        enable_rerank: rerankModel,
        top_k: recallNumber,
        score_threshold: relevanceScore,
        query: vectorQuery,
        ground_truth_texts: groundTruthTexts,
        dense_weight: denseWeight,
        keyword_weight: keywordWeight,
      });

      if (response.is_success) {
        const processedResults = response.result.map((result, idx) => {
          if (result.query_type === 'sub-query') {
            const subQueryCount =
              response.result.slice(0, idx).filter((r) => r.query_type === 'sub-query').length + 1;
            return {
              ...result,
              query_type: `sub-query-${subQueryCount}`,
            };
          }
          return result;
        });

        setResults(processedResults);
        setAnalysisData(response.analysis_data);
        setHasResults(true);
      } else {
        toast({
          title: 'Error',
          description: 'Retrieval test failed',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-auto flex-col gap-6 lg:h-[calc(100vh-220px)] lg:flex-row">
      <ConfigurationPanel
        enableEnhancement={enableEnhancement}
        setEnableEnhancement={setEnableEnhancement}
        relevanceScore={relevanceScore}
        setRelevanceScore={setRelevanceScore}
        recallNumber={recallNumber}
        setRecallNumber={setRecallNumber}
        searchType={searchType}
        setSearchType={setSearchType}
        semanticWeight={semanticWeight}
        setSemanticWeight={setSemanticWeight}
        denseWeight={denseWeight}
        setDenseWeight={setDenseWeight}
        keywordWeight={keywordWeight}
        setKeywordWeight={setKeywordWeight}
        rerankModel={rerankModel}
        setRerankModel={setRerankModel}
        groundTruthTexts={groundTruthTexts}
        setGroundTruthTexts={setGroundTruthTexts}
      />
      <QueryPanel
        vectorQuery={vectorQuery}
        setVectorQuery={setVectorQuery}
        hasResults={hasResults}
        handleRunQuery={handleRunQuery}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isLoading={isLoading}
        results={results}
        analysisData={analysisData}
        groundTruthTexts={groundTruthTexts}
        hasEmptyGroundTruthError={hasEmptyGroundTruthError}
      />
    </div>
  );
};
